import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { createSubmission } from '../store/submission.slice'
import { setPayload } from '../store/submission.slice'
import { ConfirmResponseView } from './confirm-response.view'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { formatNum, parseDate } from '../utils/functions'
import { NewSubmissionPayloadSchema, SubmissionSchema } from '../utils/types/submission'

export const ReviewFormView = () => {
  const [existingSubmission, setExistingSubmission] = useState<SubmissionSchema | null>(null)
  const [requestState, setRequestState] = useState({ error: '' })
  const sumCheckboxQuestions = useRef(0)

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const formState = useAppSelector((state) => state.form)
  const { form } = formState
  const submissionState = useAppSelector((state) => state.submission)
  const { payload, isLoading } = submissionState
  const navigate = useNavigate()
  const { formId } = useParams()

  useEffect(() => {
    if (authUser) {
      dispatch(
        setPayload({
          ...payload,
          meta: { ...payload.meta, submittedBy: authUser._id }
        })
      )
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (formId && !form) {
      navigate(`/form/${formId}/submit`, { viewTransition: true })
    }
    // eslint-disable-next-line
  }, [formId, form])

  useEffect(() => {
    if (payload.submission) {
      const allAnswers = payload.submission.sections.flatMap(({ questions }) =>
        questions.filter(({ answer }) => answer).map(({ answer }) => answer)
      )

      if (!allAnswers.length) {
        navigate(`/form/${formId}/submit`, {
          viewTransition: true,
          replace: true
        })
      }
    }
    // eslint-disable-next-line
  }, [payload.submission])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const newPayload: NewSubmissionPayloadSchema = JSON.parse(JSON.stringify(payload))

    // Preserve date selection & reset hr to 00:00
    newPayload.submission.sections.forEach(({ questions }, sectionIndex) => {
      questions.forEach(({ type, answer }, questionIndex) => {
        if (type === 'date' && typeof answer === 'string') {
          const dateObj = new Date(answer)
          const calendarArray = answer.split('-')

          dateObj.setDate(+calendarArray[2])
          dateObj.setMonth(+calendarArray[1] - 1)
          dateObj.setFullYear(+calendarArray[0])
          dateObj.setHours(0)

          newPayload.submission.sections[sectionIndex].questions[questionIndex].answer =
            dateObj.toString()
        }
      })
    })

    try {
      if (form) {
        const { data, status } = await dispatch(createSubmission(newPayload)).unwrap()

        if (status === 201) {
          form.isPublic
            ? setExistingSubmission(data)
            : navigate(`/form/${form._id}?graph=calendar`, {
                viewTransition: true
              })
        }
      }
    } catch (error: any) {
      setRequestState({ error: error.message })
    }
  }

  return (
    <>
      {existingSubmission ? (
        <ConfirmResponseView form={form} submission={existingSubmission} />
      ) : (
        <div className='main_content form_content'>
          <NavBack theme={form?.meta.colour} />
          {requestState.error && (
            <Status
              error={requestState.error}
              reset={() => setRequestState({ ...requestState, error: '' })}
            />
          )}
          {form && payload.submission && (
            <form className={`theme-content-${form.meta.colour}`} onSubmit={handleSubmit}>
              <header>
                <h1 className='heading-styled'>{form.name}</h1>
                <p>{form.description}</p>
              </header>
              {payload.submission.sections.map((section) => {
                sumCheckboxQuestions.current = 0

                return (
                  <section key={section.id}>
                    {section.meta?.showHeader && (
                      <div className='header'>
                        {section.name && <h2>{section.name}</h2>}
                        <p>{section.description}</p>
                      </div>
                    )}
                    {section.questions.map((question, questionIndex) => {
                      if (question.type === 'radio_checkbox') {
                        sumCheckboxQuestions.current++
                      }

                      return (
                        <div className='section_question' key={questionIndex}>
                          {question.type === 'radio_checkbox' ? (
                            <div />
                          ) : (
                            <span>{questionIndex + 1 - sumCheckboxQuestions.current}.</span>
                          )}
                          {question.type === 'radio_checkbox' ? (
                            <div className='checkbox_container checkbox_container-radio checkbox_container-preview'>
                              <div
                                className={`radio-custom ${question.answer ? '' : 'radio-custom-preview'}`}>
                                <input
                                  type='checkbox'
                                  defaultChecked={question.answer as boolean}
                                />
                                <span />
                                <label>
                                  <span>{question.statement}</span>
                                  {question.description && <em> ({question.description})</em>}
                                  {question.required && <span className='required'>*</span>}
                                </label>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p>
                                <span className='bold'>{question.statement}</span>
                                {question.description && <em> ({question.description})</em>}
                                {question.required && <span className='required'>*</span>}
                              </p>
                              <div />
                            </>
                          )}
                          {((typeof question.answer === 'string' ||
                            typeof question.answer === 'number') &&
                            question.answer) ||
                          (Array.isArray(question.answer) && question.answer.length) ? (
                            <span className='highlight highlight-answer'>
                              {question.type === 'file' ? (
                                <>
                                  <em className='bold'>File:</em> {question.answer}
                                </>
                              ) : (
                                <>
                                  {question.type === 'checkbox' && Array.isArray(question.answer)
                                    ? question.answer.join(', ')
                                    : question.type === 'date'
                                      ? typeof question.answer === 'string' &&
                                        parseDate(question.answer, 'short_date')
                                      : question.type === 'rating'
                                        ? `${question.answer} / ${question.meta?.ratingOptions?.length}`
                                        : question.type === 'number'
                                          ? formatNum(question.answer as number)
                                          : question.answer}
                                </>
                              )}
                            </span>
                          ) : (
                            <>{question.type !== 'radio_checkbox' && <span>-</span>}</>
                          )}
                        </div>
                      )
                    })}
                  </section>
                )
              })}
              <button className='submit button-with-icon submit-with-icon' disabled={isLoading}>
                {isLoading ? <Spinner /> : <span>Submit</span>}
                <span className='material-symbols-rounded'>check</span>
              </button>
            </form>
          )}
        </div>
      )}
    </>
  )
}
